import axios from "axios";
import { ImageConfig, ImageSize, Layer } from "../types";
import { LayerGuide } from "./LayerGuide";
import { ArchiveImage } from "./ArchiveImage";
import { ImagePickerDrawer, ImageSelectedDetail } from "./ImagePickerDrawer";
import { LAYERS, ROW_HEIGHT_FRACTION, SIZE_POOLS } from "../constants";

// References the ArchiveImage directly — _images gets reordered for z-index
// changes, so an index into it would go stale.
type ColumnLayout = {
  image: ArchiveImage;
  col: number;
  colsOccupied: number;
}

type RowLayout = ColumnLayout[];

type LayoutSlots = { [slot: string]: ImageConfig[] };

export class ArchiveManager {
  _images: ArchiveImage[] = [];
  _guides: LayerGuide[] = [];
  _scrollY: number = 0;
  _gridGap: number = 4;
  _rowIndex: number = 0;
  _layout: RowLayout[] = [];
  _layoutSlots: LayoutSlots = {};
  // The archive uses a single layout. _activeSlot tracks which stored slot key
  // holds it — new saves always write "1", but we still read whichever slot the
  // server last flagged active so previously-saved designs aren't orphaned.
  _activeSlot: string = "1";
  _isEditMode: boolean = window.SuburbiaConfig?.isEditMode || false;
  _drawer: ImagePickerDrawer | null = null;
  _swapTarget: ArchiveImage | null = null;
  _isMobile: boolean = false;

  constructor() {
    console.log("ArchiveManager 2.1.0 made with love for Suburbia by Code With Feeling {♥}");

    const saveButton = document.querySelector("#save-layout-button") as HTMLButtonElement;
    if (saveButton) {
      saveButton.addEventListener("click", this.saveLayout.bind(this));
    }

    const resetButton = document.querySelector("#reset-layout-button") as HTMLButtonElement;
    if (resetButton) {
      resetButton.addEventListener("click", this.resetLayout.bind(this));
    }

    this.loadSavedLayout();

    window.addEventListener("resize", () => this.refreshLayout());

    // The layout is restored asynchronously, so setupRowHeights may run before
    // fonts/images have settled and the grid has its final width. Re-measure once
    // everything is loaded so the live page matches the saved design exactly.
    window.addEventListener("load", () => this.refreshLayout());
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.refreshLayout());
    }
  }

  // Re-measure row heights and archive height against the current viewport.
  // Safe to call repeatedly — setupRowHeights bails when the grid is unrendered.
  refreshLayout(): void {
    if (this._isMobile || !this._images.length) {
      return;
    }
    this.setupRowHeights();
    this.updateArchiveHeight();
  }

  init() {
    // organiseImages only runs for a fresh layout; restored layouts already have _images populated.
    if (!this._images.length) {
      this.organiseImages();
    }

    this._scrollY = window.scrollY;
    window.addEventListener("scroll", this.scrollHandler.bind(this));

    this.enterFrameHandler();

    const rootEl = document.getElementById("root") as HTMLElement;
    if (rootEl && rootEl.parentNode) {
      rootEl.parentNode!.removeChild(rootEl);
    }
  }

  organiseImages() {
    SIZE_POOLS.forEach((size) => {
      const gallery = document.getElementById(size) as HTMLElement;

      if (gallery) {
        const imageBlocks = Array.from(gallery.querySelectorAll("figure"));

        const images = imageBlocks.map((figure) => new ArchiveImage({
          el: figure as HTMLElement,
          size: size as ImageSize,
        }));

        this._images.push(...images);
      }
    });

    this._images.sort(() => Math.random() - 0.5);

    if (this._isEditMode) {
      this._images.forEach(image => this.wireImageEvents(image));
    }

    // Ensure the first two images are a matched pair of landscape md/lg to anchor the layout.
    const firstImage_md_index = this._images.findIndex(img => (img.size === "md" || img.size === "lg") && !img.isPortrait);
    const firstImage = this._images.splice(firstImage_md_index, 1)[0];
    const otherSize = firstImage.size === "md" ? "lg" : "md";

    const secondImage_index = this._images.findIndex(img => img.size === otherSize && !img.isPortrait);
    const secondImage = this._images.splice(secondImage_index, 1)[0];

    // Prevent two XL images running adjacent to each other.
    const rearrangedImages: ArchiveImage[] = [];
    const preInserted = new Set<ArchiveImage>();
    let previousWasXL = false;

    this._images.forEach((img) => {
      if (preInserted.has(img)) {
        return;
      }

      if (img.size === "xl") {
        if (previousWasXL) {
          const nextNonXLIndex = this._images.findIndex(i => i.size !== "xl" && !rearrangedImages.includes(i));

          if (nextNonXLIndex !== -1) {
            const spacer = this._images[nextNonXLIndex];
            rearrangedImages.push(spacer);
            preInserted.add(spacer);
            previousWasXL = false;
          }
        }

        rearrangedImages.push(img);
        previousWasXL = true;
      } else {
        rearrangedImages.push(img);
        previousWasXL = false;
      }
    });

    this._images = [firstImage, secondImage, ...rearrangedImages];
  }

  wireImageEvents(image: ArchiveImage): void {
    image.addEventListener("imageSelected", (event: Event) => {
      const customEvent = event as CustomEvent;
      const selectedImage = customEvent.detail.image as ArchiveImage;

      this._images.forEach(archiveImage => {
        archiveImage.ui?.show(false);
      });

      selectedImage.revealUI();
      this.handleZIndexChange(selectedImage);
    });

    image.addEventListener("swapRequested", (event: Event) => {
      const selectedImage = (event as CustomEvent).detail.image as ArchiveImage;
      this._swapTarget = selectedImage;

      if (!this._drawer) {
        this._drawer = new ImagePickerDrawer();
        this._drawer.addEventListener("imageSelected", (e: Event) => {
          const { imageData } = (e as CustomEvent<ImageSelectedDetail>).detail;
          if (this._swapTarget) {
            const target = this._swapTarget;
            const fromId = parseInt(target.img.getAttribute("data-id") || "0", 10);
            target.swapImage(imageData);
            this._swapTarget = null;
            this.refreshLayout();
            this.syncSwapToPost(fromId, imageData.id, target);
          }
        });
      }

      this._drawer.open();
    });

    // Row heights depend on image aspect/size — re-measure whenever one changes
    // so the edit view always matches what a reload will compute.
    image.addEventListener("layoutChanged", () => this.refreshLayout());

    image.addEventListener("swapUndone", (event: Event) => {
      const { image: undoneImage, fromId, toId } = (event as CustomEvent).detail;
      this.syncSwapToPost(fromId, toId, undoneImage, true);
    });
  }

  // Writes an image swap back into the page's gallery blocks so the post content
  // (and its revision history) stays in step with the archive layout.
  private syncSwapToPost(fromId: number, toId: number, image: ArchiveImage, isUndo: boolean = false): void {
    const { pageId, nonce } = window.SuburbiaConfig || {};
    if (!pageId || !fromId || !toId) {
      return;
    }

    const url = `/wp-json/cwf/v1/archive/${pageId}/swap-image`;

    axios.post(url, { from: fromId, to: toId }, {
      headers: {
        "X-WP-Nonce": nonce || "",
        "Content-Type": "application/json",
      }
    })
      .then(() => {
        image.setSwapSynced(!isUndo);
      })
      .catch(error => {
        // Post content untouched — the swap still saves via swapData in the layout,
        // so nothing is lost; the backend media just won't reflect it.
        image.setSwapSynced(false);
        console.error("Error syncing swap to post content: ", error);
      });
  }

  buildMobileLayout(): void {
    this._isMobile = true;

    if (!this._images.length) {
      this.organiseImages();
    }

    const rootEl = document.getElementById("root");
    if (rootEl?.parentNode) {
      rootEl.parentNode.removeChild(rootEl);
    }

    const container = document.createElement("div");
    container.className = "mobile-archive";
    this.stageElement.insertAdjacentElement("beforebegin", container);

    const images = [...this._images];

    // Separate into heroes and cluster items
    const heroes = images.filter(img => img.size === "xl" || img.size === "lg");
    const clusterItems = images.filter(img => img.size !== "xl" && img.size !== "lg");

    // Group cluster items into chunks of 1–3
    const clusters: ArchiveImage[][] = [];
    let j = 0;
    while (j < clusterItems.length) {
      const size = Math.min(Math.floor(Math.random() * 3) + 1, clusterItems.length - j);
      clusters.push(clusterItems.slice(j, j + size));
      j += size;
    }

    // Interleave: hero, cluster, hero, cluster…
    const maxLen = Math.max(heroes.length, clusters.length);
    let heroIndex = 0;
    let isFirst = true;

    for (let k = 0; k < maxLen; k++) {
      if (k < heroes.length) {
        const side = heroIndex % 2 === 0 ? "mobile-hero--left" : "mobile-hero--right";
        const hero = document.createElement("div");
        hero.className = `mobile-hero ${side}`;
        if (!isFirst) {
          this.applyRandomSpacing(hero, false, ["mb-2", "mt-4", "mt-6", "mt-8"]);
        }
        hero.appendChild(heroes[k].el);
        container.appendChild(hero);
        heroIndex++;
        isFirst = false;
      }

      if (k < clusters.length) {
        const cluster = this.renderMobileCluster(clusters[k]);
        this.applyRandomSpacing(cluster);
        container.appendChild(cluster);
        isFirst = false;
      }
    }
  }

  private applyRandomSpacing(el: HTMLElement, forceZ: boolean = true, pool?: string[]): void {
    const defaultPool = ["-mt-4", "mb-2", "mt-4", "mt-6", "mt-8"];
    const spacingClasses = pool ?? defaultPool;
    const spacing = spacingClasses[Math.floor(Math.random() * spacingClasses.length)];
    el.classList.add(spacing);
    if (forceZ && spacing === "-mt-4") {
      el.classList.add("z-100");
    }
  }

  private renderMobileCluster(images: ArchiveImage[]): HTMLElement {
    const cluster = document.createElement("div");
    cluster.className = `mobile-cluster mobile-cluster--${images.length}`;
    const horizontalPool = ["ml-0", "ml-[5%]", "ml-[10%]", "ml-[15%]", "ml-[20%]", "ml-[25%]", "ml-[30%]", "ml-auto"];
    const randomHorizontal = () => horizontalPool[Math.floor(Math.random() * horizontalPool.length)];

    images.forEach((img, i) => {
      img.el.classList.add("cluster-item", `cluster-item--${i + 1}`);
      img.el.classList.add(randomHorizontal());
      if (i > 0) {
        this.applyRandomSpacing(img.el);
      }
      cluster.appendChild(img.el);
    });
    return cluster;
  }

  handleZIndexChange(image: ArchiveImage) {
    this._images = this._images.filter(img => img !== image);
    this._images.push(image);

    this._images.forEach(img => {
      this.stageElement.querySelector<HTMLElement>(".slots")!.appendChild(img.el);
    });
  }

  saveLayout() {
    // Single-layout mode: always write the one canonical slot so the edit view
    // and the live page can never drift onto different stored layouts.
    this._activeSlot = "1";
    const layout = this._images.map(image => image.getConfig());
    this._layoutSlots[this._activeSlot] = layout;

    const { pageId, nonce } = window.SuburbiaConfig || {};
    if (!pageId) {
      console.error("Page ID not found in SuburbiaConfig. Cannot save layout.");
      return;
    }

    const url = `/wp-json/cwf/v1/archive/${pageId}/layout`;

    axios.post(url, { layouts: this._layoutSlots, activeSlot: this._activeSlot }, {
      headers: {
        "X-WP-Nonce": nonce || "",
        "Content-Type": "application/json",
      }
    })
      .then(() => {
        const btn = document.querySelector("#save-layout-button") as HTMLButtonElement;
        btn.classList.add("bg-teal-600!", "text-white!");
        btn.innerHTML = `
        <span class="flex gap-2 items-center">
        <span>Layout Saved</span>
        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><polyline points="40 144 96 200 224 72" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>
        </span>
        `;

        setTimeout(() => {
          btn.classList.remove("bg-teal-600!", "text-white!");
          btn.textContent = "Save Layout";
        }, 3000);
      })
      .catch(error => {
        console.error("Error saving layout: ", error);
        alert("Error saving layout. Please try again.");
      });
  }

  resetLayout() {
    const { pageId, nonce } = window.SuburbiaConfig || {};
    if (!pageId) {
      console.error("Page ID not found in SuburbiaConfig. Cannot reset layout.");
      return;
    }

    if (!window.confirm("Clear the saved layout and start again with a fresh one? This cannot be undone.")) {
      return;
    }

    const url = `/wp-json/cwf/v1/archive/${pageId}/layout`;

    axios.delete(url, {
      headers: { "X-WP-Nonce": nonce || "" },
    })
      .then(() => {
        // With no saved layout the page boots into createFreshLayout on reload.
        window.location.reload();
      })
      .catch(error => {
        console.error("Error resetting layout: ", error);
        alert("Error resetting layout. Please try again.");
      });
  }

  loadSavedLayout() {
    if (this.isSmallScreen) {
      this.buildMobileLayout();
      return;
    }

    const { pageId } = window.SuburbiaConfig || {};

    if (!pageId) {
      console.error("Page ID not found in SuburbiaConfig. Cannot load layout.");
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.has("reset")) {
      console.log("Resetting to fresh layout...");
      this.createFreshLayout();
      return;
    }

    const url = `/wp-json/cwf/v1/archive/${pageId}/layout`;

    axios.get(url)
      .then(response => {
        const { layouts, layout } = response.data;

        if (layouts) {
          this._layoutSlots = layouts;
        } else if (layout && Array.isArray(layout)) {
          // Migrate legacy single-layout format to slot 1.
          this._layoutSlots = { "1": layout };
        }

        // Single-layout mode: always follow the server's last-saved active slot
        // so the edit view and the live page render the identical layout.
        if (response.data.activeSlot) {
          this._activeSlot = response.data.activeSlot;
        }

        const slotData = this._layoutSlots[this._activeSlot];
        if (slotData) {
          this.restoreSavedLayout(slotData);
        } else {
          this.createFreshLayout();
        }
      })
      .catch(error => {
        console.error("Error retrieving saved layout: ", error);
        this.createFreshLayout();
      });
  }

  restoreSavedLayout(savedLayout: ImageConfig[]): void {
    console.log("Applying saved layout...", savedLayout);

    savedLayout.forEach((image: ImageConfig) => {
      const matchingImage = document.querySelector(`img[data-id="${image.imageId}"]`) as HTMLImageElement;
      if (!matchingImage) return;

      const archiveImage: ArchiveImage = new ArchiveImage({
        el: matchingImage.closest("figure") as HTMLElement,
        size: image.size,
      });

      if (image.swapData) {
        archiveImage.swapImage({
          url: image.swapData.src,
          id: image.swapData.id,
          width: image.swapData.width,
          height: image.swapData.height,
          thumbnail: "",
          thumbWidth: 0,
          thumbHeight: 0,
          filename: "",
          aspect: image.swapData.height > image.swapData.width ? "portrait" : "landscape",
        });
      }

      if (this._isEditMode) {
        this.wireImageEvents(archiveImage);
      }

      if (image.hidden) {
        archiveImage.setHidden(true);
      }

      archiveImage.setLayer(image.layer);
      archiveImage.setTransform(image.transform);

      archiveImage.addToStage({
        stage: this.stageElement,
        index: image.imageId,
        row: image.row,
        column: image.column,
        randomisePosition: false,
        draggable: true,
      });

      this._images.push(archiveImage);
    });

    // Rebuild _layout from restored positions so setupRowHeights works on resize.
    const rowGroups: { [row: number]: RowLayout } = {};
    this._images.forEach((image) => {
      const row = image._row;
      if (!rowGroups[row]) {
        rowGroups[row] = [];
      }
      rowGroups[row].push({ image, col: image._col, colsOccupied: image.colGridCount });
    });

    this._layout = Object.keys(rowGroups)
      .map(Number)
      .sort((a, b) => a - b)
      .map(row => rowGroups[row]);

    this.setupRowHeights();
    this.updateArchiveHeight();
    this.init();
  }

  createFreshLayout(): void {
    this.init();

    let col: number = Math.floor(Math.random() * 2) + 2;
    let rowLayout = [] as RowLayout;
    let imageColsOccupied = 0;
    let layerIndex = Math.floor(Math.random() * LAYERS.length);

    this._images.forEach((image, index) => {
      image.setLayer(LAYERS[layerIndex++ % LAYERS.length]);

      col = this.checkForConflicts(this._rowIndex, col);

      if (col + image.colGridCount > 13 || (this._rowIndex === 0 && index === 2)) {
        this._layout.push(rowLayout);
        this._rowIndex++;
        rowLayout = [];
        col = Math.floor(Math.random() * 4) + 1;
        imageColsOccupied = 0;

        col = this.checkForConflicts(this._rowIndex, col);
      }

      image.addToStage({
        stage: this.stageElement,
        index,
        row: this._rowIndex + 1,
        column: col,
        randomisePosition: true,
        draggable: true,
      });

      imageColsOccupied += image.colGridCount;
      rowLayout.push({ image, col, colsOccupied: image.colGridCount });

      // xl/lg get a small gap (1) to nudge the next image over slightly;
      // smaller images get up to 4 cols gap for the Figma-style airy spread.
      const maxAddCols = ["xl", "lg"].includes(image.size) ? 1 : this._rowIndex === 0 ? 0 : 4;
      col += image.colGridCount + Math.floor(Math.random() * maxAddCols);

      // Fewer images per row (threshold 8) to keep the layout open and editorial.
      if (col > 12 || imageColsOccupied >= 8) {
        this._layout.push(rowLayout);
        this._rowIndex++;
        rowLayout = [];
        col = Math.floor(Math.random() * 5) + 1;
        imageColsOccupied = 0;
      }
    });

    if (rowLayout.length > 0) {
      this._layout.push(rowLayout);
    }

    this.setupRowHeights();

    // For fresh layouts only: bring smaller images to the front so they read
    // clearly against larger ones. Saved layouts preserve their own z-order.
    // Reorder _images itself (not just the DOM) — _images order is what gets
    // saved, so the stacking survives a save/reload round trip.
    ["sm", "xs"].forEach(size => {
      const raised = this._images.filter(img => img.size === size);
      this._images = this._images.filter(img => img.size !== size).concat(raised);
    });

    this._images.forEach(image => {
      this.slots.appendChild(image.el);
    });

    this.updateArchiveHeight();
  }

  setupRowHeights(): void {
    // If the grid isn't laid out yet (stage hidden below the md breakpoint, or
    // mid-resize across it), every offsetWidth reads 0. Measuring now would pin
    // every row to 0px — collapsing images on top of each other or hiding them
    // entirely. Bail; a later resize/load refresh will re-run once it has width.
    if (this.slots.offsetWidth <= 0) {
      return;
    }

    // Clear pinned heights first so getDisplayHeight() can measure against the current column width.
    this._images.forEach(image => {
      image.el.style.height = "";
    });

    this._layout.forEach((row, rowIndex) => {
      let maxHeight = 0;

      row.forEach((item) => {
        const imageHeight = item.image.getDisplayHeight();

        if (imageHeight > maxHeight) {
          maxHeight = imageHeight * ROW_HEIGHT_FRACTION;
        }
      });

      // Skip rows we couldn't measure rather than collapsing them to 0px.
      if (maxHeight <= 0) {
        return;
      }

      const rowElements = this.stageElement.querySelectorAll<HTMLElement>(`.row-${rowIndex + 1}`);
      rowElements.forEach((el) => {
        el.style.height = `${maxHeight}px`;

        if (rowIndex === this._layout.length - 1) {
          el.classList.add("row-final");
        }
      });
    });

    // Re-append in current _images order to restore DOM/z-index ordering.
    // _images order is the source of truth: handleZIndexChange moves clicked images
    // to the end, saveLayout captures that order, and restoreSavedLayout rebuilds it.
    this._images.forEach(image => {
      this.slots.appendChild(image.el);
    });
  }

  updateArchiveHeight(): void {
    const padding = 200;
    let maxBottom = 0;

    this._images.forEach(image => {
      // offsetTop is relative to #parallax-stage (the nearest positioned ancestor),
      // so it gives the grid position within the stage — not the full document offset.
      // We add any downward offset.y displacement (images pushed below their row).
      const offsetY_px = (image.offset.y / 100) * window.innerWidth;
      const bottom = image.el.offsetTop + image.el.offsetHeight + Math.max(0, offsetY_px);
      if (bottom > maxBottom) maxBottom = bottom;
    });

    this.stageElement.style.setProperty("--archive-height", `${maxBottom + padding}px`);
  }

  checkForConflicts(row: number, col: number): number {
    if (this._layout.length === 0) {
      return col;
    }

    const conflictExists = this._layout[row - 1].find(item => col === item.col);
    return conflictExists ? col + 1 : col;
  }

  scrollHandler() {
    this._scrollY = window.scrollY;
  }

  enterFrameHandler() {
    for (let i = 0; i < this._images.length; i++) {
      this._images[i].updateY(this._scrollY);
    }

    requestAnimationFrame(this.enterFrameHandler.bind(this));
  }

  get stageElement(): HTMLElement {
    return document.getElementById("parallax-stage") as HTMLElement;
  }

  get isSmallScreen(): boolean {
    return window.innerWidth < 768;
  }

  get slots(): HTMLElement {
    return this.stageElement.querySelector<HTMLElement>(".slots")!;
  }
}
